import type { LaneItem } from "@ondetatu/shared";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CityAutocomplete } from "../../components/CityAutocomplete.js";
import type { City } from "../../lib/api/cities.js";
import { type GroupPage as GroupPageData, groupsApi, memberToken } from "../../lib/api/groups.js";
import { Lanes } from "../lanes/Lanes.js";
import "./GroupPage.css";

export function GroupPage() {
  const { slug = "" } = useParams();
  const [page, setPage] = useState<GroupPageData | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [view, setView] = useState<"list" | "lanes">("list");
  const [name, setName] = useState("");
  const [draftCity, setDraftCity] = useState<City | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [copied, setCopied] = useState(false);
  const token = memberToken.get(slug);

  const load = useCallback(() => {
    groupsApi.page(slug).then((r) => {
      if (r.success && r.data) setPage(r.data);
      else setNotFound(true);
    });
  }, [slug]);
  useEffect(() => load(), [load]);

  async function join(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = await groupsApi.join(slug, name.trim());
    if (res.success && res.data) {
      memberToken.set(slug, res.data.member.token);
      load();
    }
  }

  async function addTravel(e: FormEvent) {
    e.preventDefault();
    if (!draftCity || !from || !token) return;
    await groupsApi.addTravel(slug, token, {
      city: draftCity.name,
      country: draftCity.country,
      country_code: draftCity.country_code,
      date_from: from,
      ...(to ? { date_to: to } : {}),
    });
    setDraftCity(null);
    setFrom("");
    setTo("");
    load();
  }

  function share() {
    navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  if (notFound) {
    return (
      <div className="gp gp--center">
        <p>This group link doesn't exist (or was removed).</p>
        <Link className="btn btn-primary" to="/">
          Go to Ondetatu
        </Link>
      </div>
    );
  }
  if (!page) return <div className="gp gp--center">Loading…</div>;

  return (
    <div className="gp">
      <header className="gp__head">
        <span className="brand brand--ink">
          <img src="/tatu.svg" alt="" aria-hidden="true" width={24} height={24} />
          Ondetatu
        </span>
        <button type="button" className="gp__share" onClick={share}>
          {copied ? "Link copied ✓" : "Share"}
        </button>
      </header>

      <h1 className="gp__title">{page.group.name}</h1>
      <div className="gp__subrow">
        <p className="gp__sub">
          {page.members.length}/{page.group.member_limit} people · who's where, when
        </p>
        {page.travel.length > 0 && (
          <fieldset className="gp__viewtoggle" aria-label="view">
            <button
              type="button"
              className={view === "list" ? "on" : ""}
              onClick={() => setView("list")}
            >
              List
            </button>
            <button
              type="button"
              className={view === "lanes" ? "on" : ""}
              onClick={() => setView("lanes")}
            >
              Lanes
            </button>
          </fieldset>
        )}
      </div>

      {view === "lanes" ? (
        <Lanes
          items={page.travel.map(
            (t): LaneItem => ({
              id: t.id,
              person_id: t.member_id,
              person_name: t.member_name,
              city: t.city,
              country: t.country,
              country_code: t.country_code,
              date_from: t.date_from,
              date_to: t.date_to,
            }),
          )}
        />
      ) : (
        <ol className="gp__schedule">
          {page.travel.length === 0 && (
            <p className="gp__muted">No trips added yet. Be the first ↓</p>
          )}
          {page.travel.map((t) => (
            <li key={t.id} className="gp__trip">
              <span className="gp__trip-who">{t.member_name}</span>
              <span className="gp__trip-where">
                <b>{t.city}</b>
                {t.country ? `, ${t.country}` : ""}
              </span>
              <span className="gp__trip-when">
                {t.date_from} → {t.date_to ?? "…"}
              </span>
            </li>
          ))}
        </ol>
      )}

      {!token ? (
        page.full ? (
          <p className="gp__muted">This group is full.</p>
        ) : (
          <form className="gp__card" onSubmit={join}>
            <h2 className="gp__h2">Add yourself</h2>
            <input
              className="gp__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              aria-label="your name"
              maxLength={60}
            />
            <button type="submit" className="btn btn-primary" disabled={!name.trim()}>
              Join — no account needed
            </button>
          </form>
        )
      ) : (
        <form className="gp__card" onSubmit={addTravel}>
          <h2 className="gp__h2">Add your travel</h2>
          <CityAutocomplete label="your destination" onSelect={setDraftCity} />
          <div className="gp__row">
            <input
              type="date"
              aria-label="from"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <input type="date" aria-label="to" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={!draftCity || !from}>
            Add trip
          </button>
        </form>
      )}

      <Link className="gp__cta" to="/">
        ✨ Want the full map of all your people? Get an invite →
      </Link>
    </div>
  );
}
